import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const VerifyAccountDeletion = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<'loading' | 'verified' | 'deleting' | 'deleted' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid verification link');
      return;
    }

    try {
      // Verify the token and mark as verified
      const { data, error } = await supabase
        .from('account_deletion_requests')
        .update({
          is_verified: true,
          verified_at: new Date().toISOString()
        })
        .eq('verification_token', token)
        .eq('is_verified', false)
        .gte('expires_at', new Date().toISOString())
        .select()
        .single();

      if (error || !data) {
        setStatus('error');
        setErrorMessage('Invalid or expired verification link');
        return;
      }

      setStatus('verified');
    } catch (error) {
      console.error('Error verifying token:', error);
      setStatus('error');
      setErrorMessage('Failed to verify deletion request');
    }
  };

  const handleDeleteAccount = async () => {
    setStatus('deleting');

    try {
      const { error } = await supabase.rpc('delete_user_account');

      if (error) {
        console.error('Error deleting account:', error);
        setStatus('error');
        setErrorMessage(error.message || 'Failed to delete account');
        return;
      }

      setStatus('deleted');
      
      // Sign out after a brief delay
      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate('/', { replace: true });
      }, 3000);

    } catch (error: any) {
      console.error('Error deleting account:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Failed to delete account');
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="text-center">
            {status === 'loading' && 'Verifying Request...'}
            {status === 'verified' && '⚠️ Account Deletion'}
            {status === 'deleting' && 'Deleting Account...'}
            {status === 'deleted' && '✓ Account Deleted'}
            {status === 'error' && 'Verification Failed'}
          </CardTitle>
          <CardDescription className="text-center">
            {status === 'loading' && 'Please wait while we verify your deletion request'}
            {status === 'verified' && 'Are you absolutely sure?'}
            {status === 'deleting' && 'Please wait...'}
            {status === 'deleted' && 'Your account has been successfully deleted'}
            {status === 'error' && errorMessage}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
            </div>
          )}

          {status === 'verified' && (
            <>
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                  <div>
                    <p className="font-semibold text-destructive">This action cannot be undone!</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      All your data will be permanently deleted, including:
                    </p>
                    <ul className="text-sm text-muted-foreground mt-2 space-y-1 ml-4 list-disc">
                      <li>All forms and submissions</li>
                      <li>All contacts and response data</li>
                      <li>WhatsApp integrations and templates</li>
                      <li>Payment history and subscription</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="flex-1"
                >
                  Cancel - Keep My Account
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteAccount}
                  className="flex-1"
                >
                  Yes, Delete Forever
                </Button>
              </div>
            </>
          )}

          {status === 'deleting' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-destructive" />
              <p className="text-sm text-muted-foreground">
                Deleting your account and all associated data...
              </p>
            </div>
          )}

          {status === 'deleted' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <CheckCircle className="h-12 w-12 text-success" />
              <p className="text-center text-sm text-muted-foreground">
                Your account has been permanently deleted. You will be redirected shortly.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <XCircle className="h-12 w-12 text-destructive" />
              <Button onClick={() => navigate('/')} variant="outline">
                Return to Home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyAccountDeletion;
